import { defineStore } from "pinia";
import { publicBoaHttp, privateBoaHttp, trpcClient } from "@/boa-api-client";
import fixedEncodeURIComponent from "@/util/fixed-encode-uri-component";

import useAuthStore from "@/stores/auth";
import useCategoriesStore from "@/stores/categories";
import useAwardStore from "@/stores/award";

import type {
  Customer,
  Invoice,
  Charge,
  PaymentMethod,
  Contact,
  AddedProduct,
  PurchaseStep,
  BOALocation,
  Candidate,
  CandidateInstance,
  CandidateClaim,
  CandidateRegistration,
} from "best-of-awards-api";

import fixedEncodeUriComponent from "@/util/fixed-encode-uri-component";
import { HTTPError } from "ky";
import type { SetupIntent } from "@stripe/stripe-js";

type PurchaseResponse = {
  success: boolean;
  invoice?: { invoice_pdf?: string; hosted_invoice_url?: string };
  message?: string;
};

type VotingStageSignupData = Record<string, any> & {
  notifications?: string;
  regions?: string[];
  signature?: string;
  bonus?: boolean;
  bonusCall?: boolean;
};

function isPurchaseResponse(response?: unknown): response is PurchaseResponse {
  return !!response && typeof response === "object" && "success" in response;
}

type State = {
  acceptedCandidates: Candidate[];
  currentCandidate: Candidate;
  candidateLoading: boolean;
  existingCandidates: Candidate[];
  newCandidates: Candidate[];
  nominees: any[];
  selectedProducts: AddedProduct[];
  votingStageSignupData: VotingStageSignupData | null;
};

export type VoterRewardsSettings = {
  bidAmount: number;
  billingStatus: "red" | "green";
  budgetRefresh: number;
  credits: number;
  currentSpend: number;
  enabled: boolean;
  ip: string;
  maximumBudget: number;
  minimumBidAmount: number;
  notifications: string;
  paymentMethod?: string;
  regions: Array<string>;
  signature: string;
  signupStatus: string;
};

export type CandidateSearchResult = {
  categories: string[];
  claimedYears: number[];
  id: string;
  locations: string[];
  name: string;
  name_exact: string;
  nominatedYears: number[];
  tags: string[];
};

type CandidateSearchResponse = {
  results: CandidateSearchResult[];
  total: number;
};

export function getCandidateCeremonyNight(candidate: Candidate) {
  const awardStore = useAwardStore();
  const { awardsCeremonyNights = [] } = awardStore.activeAwardInstance || {};
  if (!awardsCeremonyNights?.length) {
    // no nights defined, default to first night
    return 0;
  }
  const categoryIds = awardsCeremonyNights.flatMap((n) => n.categoryIds);
  if (!categoryIds.length) {
    // no categories specified for any night, default to first night
    return 0;
  }
  const finalistCategory = candidate.categories?.find(
    (c) => c.finalistsYears?.[`${awardStore.awardYear}`]?.length > 0
  );
  if (!finalistCategory) {
    return -1;
  }
  const night = awardsCeremonyNights.findIndex((n) =>
    n.categoryIds.includes(finalistCategory.categoryId)
  );
  return night;
}

const emptyCandidate = {
  PK: "",
  awards: [],
  bannedUntil: null,
  categories: [],
  claims: [],
  contacts: [],
  currentQueue: "",
  customers: [],
  displayName: "",
  id: "",
  instances: [],
  locations: [],
  name: "",
  paymentMethods: {
    data: [],
  },
  products: [],
  registrations: [],
  results: [],
  tickets: [],
  type: "Company",
  webLogoUrl: "",
  webLogoDataURL: "",
};

export default defineStore("candidates", {
  state: (): State => ({
    acceptedCandidates: [],
    currentCandidate: { ...emptyCandidate } as Candidate,
    candidateLoading: false,
    existingCandidates: [],
    newCandidates: [],
    nominees: [],
    selectedProducts: [],
    votingStageSignupData: null,
  }),
  getters: {
    filteredExistingCandidates: (state) => {
      const categoriesStore = useCategoriesStore();
      const awardStore = useAwardStore();
      return state.existingCandidates.filter(
        (candidate) =>
          !candidate.categories.length ||
          candidate.categories.some((category) => {
            const { awardYears = [] } =
              categoriesStore.awardYearCategories.find(
                (c) => c.id === category.categoryId
              ) || {};
            return awardYears.includes(awardStore.awardYear);
          })
      );
    },
    filteredNewCandidates: (state) => {
      return state.newCandidates;
    },
    currentCandidateInstance: (state): CandidateInstance | undefined => {
      if (!state.currentCandidate) {
        return;
      }
      const { instances = [] } = state.currentCandidate;
      if (!instances.length) {
        return;
      }
      const awardStore = useAwardStore();
      return instances.find((i) => parseInt(i.SK, 10) === awardStore.awardYear);
    },
    currentCandidateCeremonyNight: (state): number => {
      return getCandidateCeremonyNight(state.currentCandidate);
    },
    customer: (state): Customer | undefined => {
      return state.currentCandidate?.customers?.[0];
    },
    invoices: (state): Invoice[] => {
      const authStore = useAuthStore();
      if (authStore.isCandidate) {
        return state.currentCandidate?.customers?.[0]?.invoices || [];
      } else if (authStore.isAdmin) {
        const invoices =
          state.currentCandidate?.customers?.flatMap(
            (customer) => customer.invoices
          ) || [];
        invoices.sort((a, b) => b.created - a.created);
        return invoices;
      }
      return [];
    },
    charges: (state): Charge[] => {
      const authStore = useAuthStore();
      if (authStore.isCandidate) {
        return state.currentCandidate?.customers?.[0]?.charges || [];
      } else if (authStore.isAdmin) {
        const charges =
          state.currentCandidate?.customers?.flatMap(
            (customer) => customer.charges
          ) || [];
        charges.sort((a, b) => b.created - a.created);
        return charges;
      }
      return [];
    },
    paymentMethods: (state): PaymentMethod[] => {
      const authStore = useAuthStore();
      if (authStore.isCandidate) {
        return state.currentCandidate?.customers?.[0]?.paymentMethods || [];
      } else if (authStore.isAdmin) {
        const paymentMethods =
          state.currentCandidate?.customers?.flatMap(
            (customer) => customer.paymentMethods
          ) || [];
        paymentMethods.sort((a, b) => b.created - a.created);
        return paymentMethods;
      }
      return [];
    },
    setupIntents: (state): SetupIntent[] => {
      const authStore = useAuthStore();
      if (authStore.isCandidate) {
        return state.currentCandidate?.customers?.[0]?.setupIntents || [];
      } else if (authStore.isAdmin) {
        const setupIntents =
          state.currentCandidate?.customers?.flatMap(
            (customer) => customer.setupIntents
          ) || [];
        setupIntents.sort((a, b) => b.created - a.created);
        return setupIntents;
      }
      return [];
    },
    claimedNomination: (state): boolean => {
      const awardStore = useAwardStore();
      const claims = state.currentCandidate?.claims || [];
      return claims.some((c) => c.awardYear === awardStore.awardYear);
    },
    isNominee: (state): boolean => {
      const awardStore = useAwardStore();
      const categories = state.currentCandidate?.categories || [];
      return categories.some((c) =>
        c.finalizedYears?.includes(awardStore.awardYear)
      );
    },
    isWinner: (state): boolean => {
      const awardStore = useAwardStore();
      const categories = state.currentCandidate?.categories || [];
      return categories.some((c) =>
        c.winnersYears?.includes(awardStore.awardYear)
      );
    },
    isFinalist: (state): boolean => {
      const awardStore = useAwardStore();
      const categories = state.currentCandidate?.categories || [];
      return categories.some(
        (c) => c.finalistsYears?.[awardStore.awardYear]?.length > 0
      );
    },
    isPreviousWinner: (state): boolean => {
      return state.currentCandidate?.results?.some((result) => result.winner);
    },
    isPreviousFinalist: (state): boolean => {
      return state.currentCandidate?.results?.some((result) => result.finalist);
    },
  },
  actions: {
    async getCandidate(idOrPK?: string) {
      try {
        let candidateId = idOrPK;
        const match = idOrPK?.match(/CANDIDATE#(.*)/);
        if (match) {
          candidateId = match[1];
        }
        if (!candidateId) {
          const authStore = useAuthStore();
          if (authStore.user?.candidateId) {
            candidateId = authStore.user.candidateId;
          } else if (this.currentCandidate.id) {
            candidateId = this.currentCandidate.id;
          }
        }
        this.candidateLoading = true;
        const response: { success: boolean; candidate: Candidate } =
          await publicBoaHttp.get(`candidates/${candidateId}`).json();
        this.candidateLoading = false;
        if (response.success) {
          this.currentCandidate = {
            ...response.candidate,
            customers: this.currentCandidate.customers,
          };
          return response.candidate;
        }
      } catch (error) {
        console.error(error);
        this.candidateLoading = false;
      }
    },
    // async getCandidate(idOrPK?: string) {
    //   try {
    //     let candidateId = idOrPK;
    //     const match = idOrPK?.match(/CANDIDATE#(.*)/);
    //     if (match) {
    //       candidateId = match[1];
    //     }
    //     if (!candidateId) {
    //       const authStore = useAuthStore();
    //       if (authStore.user?.candidateId) {
    //         candidateId = authStore.user.candidateId;
    //       } else if (this.currentCandidate.id) {
    //         candidateId = this.currentCandidate.id;
    //       } else {
    //         throw new Error("No candidateId provided");
    //       }
    //     }
    //     this.candidateLoading = true;
    //     const candidate = await trpcClient.getCandidate.query(candidateId);
    //     this.candidateLoading = false;
    //     this.currentCandidate = {
    //       ...candidate,
    //       customers: this.currentCandidate.customers,
    //     };
    //     return candidate;
    //   } catch (error) {
    //     console.error(error);
    //     this.candidateLoading = false;
    //   }
    // },
    async getCandidateCustomers(idOrPK?: string) {
      try {
        let candidateId = idOrPK;
        const match = idOrPK?.match(/CANDIDATE#(.*)/);
        if (match) {
          candidateId = match[1];
        }
        if (!candidateId) {
          const authStore = useAuthStore();
          if (authStore.user?.candidateId) {
            candidateId = authStore.user.candidateId;
          } else if (this.currentCandidate.id) {
            candidateId = this.currentCandidate.id;
          }
        }
        const response: { success: boolean; customers: Array<Customer> } =
          await privateBoaHttp
            .get(`candidates/${candidateId}/customers`)
            .json();
        if (response.success) {
          this.currentCandidate.customers = response.customers;
          return response.customers;
        }
        return null;
      } catch (error) {
        console.error(error);
      }
    },
    async getCandidateByName(name: string) {
      try {
        const response = await publicBoaHttp
          .get(`candidates/name/${fixedEncodeUriComponent(name)}`)
          .json<{ candidate?: Candidate }>();
        return response.candidate;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async searchCandidates({
      queryText = "",
      claimedYear = "",
      nominatedYear = "",
      tags = [],
      category = "",
      pageNum = 1,
      sortByName = false,
    }) {
      let tagQuery = "";
      if (tags.length) {
        tagQuery = fixedEncodeURIComponent(JSON.stringify(tags));
      }
      let categoryQuery = "";
      if (category) {
        categoryQuery = fixedEncodeURIComponent(JSON.stringify([category]));
      }
      const queryString = `queryText=${fixedEncodeURIComponent(
        queryText
      )}&claimedYear=${claimedYear}&nominatedYear=${nominatedYear}&tagQuery=${tagQuery}&categoryQuery=${categoryQuery}&pageNum=${pageNum}&sortByName=${
        sortByName ? "true" : ""
      }`;
      const { results, total }: CandidateSearchResponse = await publicBoaHttp
        .get(`candidates?${queryString}`)
        .json();
      return { results, total };
    },
    async searchGooglePlaces({ query = "", geoModifiers = "" }) {
      const googlePlaceMatches = await privateBoaHttp
        .get(
          `search-google-places?queryText=${fixedEncodeUriComponent(
            query
          )}&geoModifiers=${fixedEncodeUriComponent(geoModifiers)}`
        )
        .json();
      return googlePlaceMatches;
    },
    async getGooglePlaceDetails({
      placeId,
      locationKey = { PK: "", SK: "" },
      force = false,
    }: {
      placeId: string;
      locationKey: { PK: string; SK: string };
      force: boolean;
    }) {
      if (!placeId) {
        return null;
      }
      try {
        const googlePlaceDetails: any = await privateBoaHttp
          .get("google-place-details", {
            searchParams: {
              placeId,
              PK: locationKey.PK || "",
              SK: locationKey.SK || "",
              force,
            },
          })
          .json();
        if (googlePlaceDetails.message) {
          throw new Error(googlePlaceDetails.message);
        }
        return googlePlaceDetails;
      } catch (error) {
        return null;
      }
    },
    async getExistingCandidates({ categoryId = "all", pageToken = null }) {
      try {
        const searchParams = new URLSearchParams();
        if (pageToken) {
          searchParams.append("pageToken", JSON.stringify(pageToken));
        }
        let response: {
          success: boolean;
          candidates: Candidate[];
          pageToken: any;
        } = await privateBoaHttp
          .get(`existing-candidates/${categoryId}`, {
            searchParams,
          })
          .json();
        const { success, candidates } = response;
        if (success) {
          this.existingCandidates = candidates;
        }
        let nextPageToken = response.pageToken;
        while (nextPageToken) {
          searchParams.set("pageToken", JSON.stringify(nextPageToken));
          response = await privateBoaHttp
            .get(`existing-candidates/${categoryId}`, {
              searchParams,
            })
            .json();
          this.existingCandidates = this.existingCandidates.concat(
            response.candidates
          );
          nextPageToken = response.pageToken;
        }
        return response.success;
      } catch (error) {
        console.error(error);
      }
    },
    async getNewCandidates({ categoryId = "all" }) {
      try {
        const searchParams = new URLSearchParams();
        const response: {
          success: boolean;
          candidates: Candidate[];
        } = await privateBoaHttp
          .get(`new-candidates/${categoryId}`, {
            searchParams,
          })
          .json();
        const { success, candidates } = response;
        if (success) {
          this.newCandidates = candidates;
          return candidates;
        }
        return [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    async getNominees({
      categoryId,
      awardYear,
    }: {
      categoryId: string;
      awardYear: number;
    }) {
      try {
        let queryString = "";
        if (awardYear) {
          queryString += `?awardYear=${awardYear}`;
        }
        const response: {
          success: boolean;
          acceptedNominees: Candidate[];
          nominees: Candidate[];
        } = await privateBoaHttp
          .get(`nominees/${categoryId}${queryString}`)
          .json();
        if (response.success) {
          return {
            acceptedNominees: response.acceptedNominees,
            nominees: response.nominees,
          };
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getFinalists({
      categoryId,
      awardYear,
    }: {
      categoryId: string;
      awardYear: number;
    }) {
      try {
        let queryString = "";
        if (awardYear) {
          queryString += `?awardYear=${awardYear}`;
        }
        const response: { success: boolean; finalists: Candidate[] } =
          await privateBoaHttp
            .get(`finalists/${categoryId}${queryString}`)
            .json();
        if (response.success) {
          return response;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getMasterCategoryFinalists({
      masterCategory,
      awardYear,
    }: {
      masterCategory: any;
      awardYear: number;
    }) {
      try {
        let queryString = "";
        if (awardYear) {
          queryString += `?awardYear=${awardYear}`;
        }

        const { categories } = useCategoriesStore();
        const { regions } = useAwardStore();

        const results = await Promise.allSettled(
          masterCategory.categories.map(async (categoryName: string) => {
            const category = categories.find((c) => c.name === categoryName);
            if (!category) {
              throw new Error(`Invalid category ${categoryName}`);
            }
            const response: { success: boolean; finalists: Candidate[] } =
              await privateBoaHttp
                .get(`finalists/${category.id}${queryString}`)
                .json();
            if (!response.success) {
              throw new Error(`Failed to fetch finalists for ${categoryName}`);
            }
            return { categoryId: category.id, response };
          })
        );

        let finalists: Candidate[] = [];
        for (const result of results) {
          if (result.status === "fulfilled") {
            const regionalWinners = result.value.response.finalists.filter(
              (f: Candidate) => {
                const category = f.categories.find(
                  (c) => c.categoryId === result.value.categoryId
                );
                if (regions.length > 1) {
                  return (
                    category &&
                    category.finalistsYears &&
                    category.finalistsYears[awardYear]
                  );
                } else {
                  return (
                    category &&
                    category.winnersYears &&
                    category.winnersYears.includes(awardYear)
                  );
                }
              }
            );
            finalists = finalists.concat(regionalWinners);
          }
        }

        return finalists.reduce((fs: Candidate[], finalist: Candidate) => {
          const f = fs.find((f: Candidate) => f.id === finalist.id);
          if (f) {
            f.votes = f.votes.concat(finalist.votes);
          } else {
            fs.push(finalist);
          }
          return fs;
        }, []);
      } catch (error) {
        console.error(error);
      }
    },
    async createCandidate(candidate: any) {
      try {
        const response: { candidateId: string } = await privateBoaHttp
          .put("candidates", { json: { ...candidate } })
          .json();
        return response.candidateId;
      } catch (error) {
        console.error(error);
      }
    },
    async updateCandidate(candidate: Partial<Candidate>, awardYear?: number) {
      const response: { candidate: Candidate } = await privateBoaHttp
        .post(`candidates/${candidate.id}`, { json: { candidate, awardYear } })
        .json();
      let index = this.existingCandidates.findIndex(
        (c) => c.id === candidate.id
      );
      if (index > -1 && this.existingCandidates[index].submitted) {
        this.existingCandidates[index] = response.candidate;
      }
      index = this.newCandidates.findIndex((c) => c.id === candidate.id);
      if (index > -1 && !this.newCandidates[index].accepted) {
        this.newCandidates[index] = response.candidate;
      }
      if (this.currentCandidate) {
        this.currentCandidate = response.candidate;
      }
      return response;
    },
    async updateCandidateCategories(
      candidate: Partial<Candidate>,
      awardYear?: number
    ) {
      const response: { success: boolean } = await privateBoaHttp
        .post(`candidates/${candidate.id}/categories`, {
          json: { candidate, awardYear },
        })
        .json();
      return response;
    },
    async deleteCandidate(candidateId: string) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .delete(`candidates/${candidateId}`)
          .json();
        if (response.success) {
          let index = this.existingCandidates.findIndex(
            (c) => c.id === candidateId
          );
          if (index > -1) {
            this.existingCandidates.splice(index, 1);
          }
          index = this.newCandidates.findIndex((c) => c.id === candidateId);
          if (index > -1) {
            this.newCandidates.splice(index, 1);
          }
        }
        return response.success;
      } catch (error) {
        console.error(error);
      }
    },
    async rejectCandidate(candidate: Candidate) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/reject/${candidate.id}`, { json: { candidate } })
          .json();
        if (response.success) {
          const index = this.newCandidates.findIndex(
            (c) => c.id === candidate.id
          );
          if (index > -1) {
            this.newCandidates.splice(index, 1);
          }
        }
        return response.success;
      } catch (error) {
        console.error(error);
      }
    },
    async acceptNewCandidate({
      candidate,
      duplicateCandidate,
      awardYear,
      skipExistingQueue = false,
    }: {
      candidate: Candidate;
      duplicateCandidate: Candidate;
      awardYear: number;
      skipExistingQueue: boolean;
    }): Promise<{ success: boolean; message?: string }> {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/accept/${candidate.id}`, {
            json: {
              candidate,
              duplicateCandidate,
              awardYear,
              skipExistingQueue,
            },
          })
          .json();
        // if (response.success) {
        //   const index = this.newCandidates.findIndex((c) => {
        //     if (duplicateCandidate) {
        //       return c.id === duplicateCandidate.id;
        //     } else {
        //       return c.id === candidate.id;
        //     }
        //   });
        //   if (index > -1) {
        //     this.newCandidates.splice(index, 1);
        //   }
        // }
        return { success: response.success };
      } catch (error) {
        console.error(error);
        if (error instanceof HTTPError) {
          const response: { message: string } = await error.response.json();
          return { success: false, message: response.message };
        }
        return { success: false, message: "Unknown error" };
      }
    },
    async acceptExistingCandidate({
      candidate,
      awardYear,
    }: {
      candidate: Candidate;
      awardYear: number;
    }): Promise<{ success: boolean; message?: string }> {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/accept/${candidate.id}`, {
            json: { candidate, awardYear },
          })
          .json();
        // if (response.success) {
        //   const index = this.existingCandidates.findIndex(
        //     (c) => c.id === candidate.id
        //   );
        //   if (index > -1) {
        //     this.existingCandidates.splice(index, 1);
        //   }
        // }
        return { success: response.success };
      } catch (error) {
        console.error(error);
        if (error instanceof HTTPError) {
          const response: { message: string } = await error.response.json();
          return { success: false, message: response.message };
        }
        return { success: false, message: "Unknown error" };
      }
    },
    async quickNominate(params: {
      candidateId?: string;
      candidateName?: string;
      categoryName: string;
      categoryType?: "Primary" | "Secondary";
      city?: string;
      accepted?: boolean;
      verified?: boolean;
      awardYear?: number;
    }) {
      const response = await privateBoaHttp
        .post("candidates/quick-nominate", {
          json: params,
        })
        .json<{ success: boolean; message?: string }>();
      return response;
    },
    async nominateExistingCandidates({
      candidates,
      categoryName,
      accepted,
      awardYear,
    }: {
      candidates: any;
      categoryName: string;
      accepted: boolean;
      awardYear: number;
    }) {
      try {
        await privateBoaHttp.post("candidates/nominate", {
          json: { candidates, categoryName, accepted, awardYear },
        });
        return true;
      } catch (error) {
        console.error(error);
      }
    },
    async finalizeNominees({
      nominees,
      category,
      complete,
      finalizing,
      preselecting,
    }: {
      nominees: Candidate[];
      category: Category;
      complete: boolean;
      finalizing: boolean;
      preselecting: boolean;
    }) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post("nominees/finalize", {
            json: {
              nominees,
              category,
              complete,
              finalizing,
              preselecting,
            },
          })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
      }
    },
    async finalizeFinalists({
      finalists,
      category,
      complete = false,
    }: {
      finalists: Candidate[];
      category: Category;
      complete: boolean;
    }) {
      try {
        await privateBoaHttp.post("finalists/finalize", {
          json: {
            finalists,
            category,
            complete,
          },
        });
        return { success: true };
      } catch (error: any) {
        const { message } = await error.response.json();
        return { success: false, message };
      }
    },
    async finalizeWinners({
      finalists,
      category,
      complete = false,
    }: {
      finalists: Candidate[];
      category: Category;
      complete: boolean;
    }) {
      try {
        await privateBoaHttp.post("winners/finalize", {
          json: {
            finalists,
            category,
            complete,
          },
        });
        return { success: true };
      } catch (error: any) {
        const { message } = await error.response.json();
        return { success: false, message };
      }
    },
    async finalizeMasterCategoryWinners({
      finalists,
      selectedWinners,
      selectedRegionalWinners,
      masterCategory,
      complete = false,
    }: {
      finalists: Candidate[];
      selectedWinners: any;
      selectedRegionalWinners: any;
      masterCategory: MasterCategory;
      complete: boolean;
    }) {
      try {
        await privateBoaHttp.post("masterCategoryWinners/finalize", {
          json: {
            finalists,
            selectedWinners,
            selectedRegionalWinners,
            masterCategory,
            complete,
          },
        });
        return { success: true };
      } catch (error: any) {
        const { message } = await error.response.json();
        return { success: false, message };
      }
    },
    async createContact(contact: Partial<Contact>) {
      try {
        await privateBoaHttp.put("contacts", { json: contact }).json();
        return { success: true };
      } catch (error: any) {
        const { message } = await error.response.json();
        return { success: false, message };
      }
    },
    async updateContact(
      contact: Partial<Contact> & { candidateId: string; canLogIn: boolean }
    ) {
      try {
        const response: { newPK: string } = await privateBoaHttp
          .post(`contacts/${fixedEncodeURIComponent(contact.PK!)}`, {
            json: contact,
          })
          .json();
        return { success: true, newPK: response.newPK };
      } catch (error: any) {
        const { message } = await error.response.json();
        return { success: false, message };
      }
    },
    async deleteContact(contact: Partial<Contact> & { candidateId: string }) {
      const response: { success: boolean } = await privateBoaHttp
        .delete("contacts", { json: contact })
        .json();
      return response.success;
    },
    async checkPlaceIds(location: BOALocation) {
      try {
        const response = await privateBoaHttp.post(
          "locations/check-place-ids",
          {
            json: location,
          }
        );
        const { locations } = await response.json<{
          locations: Array<BOALocation>;
        }>();
        return locations;
      } catch (error: any) {
        const { message } = await error.response.json();
        return { success: false, message };
      }
    },
    async createLocation(location: BOALocation) {
      try {
        const response = await privateBoaHttp
          .put("locations", { json: location })
          .json<{
            location: BOALocation;
            otherLocations: Array<
              BOALocation & { candidateId: string; candidateName: string }
            >;
          }>();
        return response;
      } catch (error) {
        console.error(error);
      }
    },
    async updateLocation(location: BOALocation) {
      try {
        const match = location.SK.match(/LOCATION#(.*)/);
        if (!match) {
          throw new Error(`Invalid location ${location.SK}`);
        }
        const locationId = match[1];
        const response = await privateBoaHttp
          .post(`locations/${locationId}`, { json: location })
          .json<{
            location: BOALocation;
            otherLocations: Array<
              BOALocation & { candidateId: string; candidateName: string }
            >;
          }>();
        return response;
      } catch (error) {
        console.error(error);
      }
    },
    async deleteLocation({
      candidateId,
      locationId,
    }: {
      candidateId: string;
      locationId: string;
    }) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .delete(`locations/${locationId}`, { json: { candidateId } })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
      }
    },
    async scrapeCandidates({
      candidateIds,
      candidateType,
    }: {
      candidateIds?: string[];
      candidateType?: "candidate" | "acceptedNominee" | "nominee" | "finalist";
    }) {
      try {
        if (!candidateIds?.length && !candidateType) {
          throw new Error("candidateIds or candidateType is required");
        }
        return privateBoaHttp
          .post("candidates/scrape/", { json: { candidateIds, candidateType } })
          .json() as Promise<{ jobs: any }>;
      } catch (error: unknown) {
        if (error instanceof HTTPError) {
          return error.response.json();
        }
        throw error;
      }
    },
    async scrapeCandidatesBBB({
      candidateIds,
      candidateType,
    }: {
      candidateIds?: string[];
      candidateType?: "acceptedNominee" | "nominee" | "finalist";
    }) {
      try {
        if (!candidateIds?.length && !candidateType) {
          throw new Error("candidateIds or candidateType is required");
        }
        return privateBoaHttp
          .post("candidates/scrape/bbb", {
            json: { candidateIds, candidateType },
          })
          .json();
      } catch (error: unknown) {
        if (error instanceof HTTPError) {
          return error.response.json();
        }
        throw error;
      }
    },
    async terminateScrape({
      candidateId,
      jobId,
    }: {
      candidateId: string;
      jobId: string;
    }) {
      try {
        return privateBoaHttp
          .delete(`candidates/${candidateId}/scrape/${jobId}`)
          .json();
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async getCandidateEmployees(candidateId: string) {
      try {
        const response: { employees: any[]; surveysSent: boolean } =
          await privateBoaHttp
            .get(`candidates/${candidateId}/employees`)
            .json();
        return response;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async createCandidateEmployees({
      candidateId,
      employees,
    }: {
      candidateId: string;
      employees: any[];
    }) {
      const response: { success: boolean } = await privateBoaHttp
        .put(`candidates/${candidateId}/employees`, { json: { employees } })
        .json();
      return response.success;
    },
    async updateCandidateEmployees({
      candidateId,
      employees,
    }: {
      candidateId: string;
      employees: any[];
    }) {
      const response: { success: boolean } = await privateBoaHttp
        .post(`candidates/${candidateId}/employees`, { json: { employees } })
        .json();
      return response.success;
    },
    async deleteCandidateEmployee({
      candidateId,
      employee,
    }: {
      candidateId: string;
      employee: any;
    }) {
      const response: { success: boolean } = await privateBoaHttp
        .delete(`candidates/${candidateId}/employees`, { json: { employee } })
        .json();
      return response.success;
    },
    async updateEmailSettings({
      candidateId,
      emailSettings,
    }: {
      candidateId: string;
      emailSettings: any;
    }) {
      const response: { success: boolean } = await privateBoaHttp
        .post(`candidates/${candidateId}/email-settings`, {
          json: { emailSettings },
        })
        .json();
      return response.success;
    },
    async sendEmployeeEmails({
      candidateId,
      employees,
    }: {
      candidateId: string;
      employees: any[];
    }) {
      try {
        const response: { count: number } = await privateBoaHttp
          .post(`candidates/${candidateId}/employees/email`, {
            json: { employees },
          })
          .json();
        return response;
      } catch (error: any) {
        const { message } = await error.response.json();
        return { message };
      }
    },
    async mergeCandidates({
      candidateIds,
      targetLocations,
      targetProps,
    }: {
      candidateIds: string[];
      targetLocations: BOALocation[];
      targetProps: any;
    }) {
      try {
        const response: { success: boolean; newCandidateId: string } =
          await privateBoaHttp
            .post("candidates/merge", {
              json: {
                candidateIds,
                targetLocations,
                targetProps,
              },
            })
            .json();
        return response;
      } catch (error) {
        if (error instanceof HTTPError) {
          const response = await error.response.json();
          return response;
        }
      }
    },
    async claimNomination({
      candidateId,
      claimed = false,
      data,
      locations = [],
      awardYear,
    }: {
      candidateId?: string;
      claimed: boolean;
      data: any;
      locations: Array<Partial<BOALocation> & { reason?: string }>;
      awardYear: number;
    }) {
      const response: {
        success: boolean;
        claim: CandidateClaim;
        userCreated: boolean;
        userExists: boolean;
      } = await publicBoaHttp
        .post("candidates/claim-nomination", {
          json: {
            candidateId,
            claimed,
            data,
            locations,
            awardYear,
          },
        })
        .json();
      return response;
    },
    async register({
      candidateId,
      data,
      locations = [],
    }: {
      candidateId: string;
      data: any;
      locations: Array<Partial<BOALocation>>;
    }) {
      const response: {
        success: boolean;
        registration: CandidateRegistration;
        userCreated: boolean;
        userExists: boolean;
      } = await publicBoaHttp
        .post("candidates/register", {
          json: {
            candidateId,
            data,
            locations,
          },
        })
        .json();
      return response;
    },
    async initVoterRewards({
      candidateId,
      customerId,
    }: {
      candidateId: string;
      customerId: string;
    }) {
      if (!this.votingStageSignupData) {
        throw new Error("Missing votingStageSignupData");
      }
      const response: {
        success: boolean;
        signupStatus: string;
        enabled: boolean;
      } = await privateBoaHttp
        .post("candidates/init-voter-rewards", {
          json: {
            candidateId,
            customerId,
            ...this.votingStageSignupData,
          },
        })
        .json();
      this.currentCandidate.voterRewards = {
        ...this.currentCandidate.voterRewards,
        signupStatus: response.signupStatus,
        enabled: response.enabled,
      };
      return response;
    },
    async submitManualClaim(candidate: Partial<Candidate>, data: any) {
      await publicBoaHttp
        .post("candidates/submit-manual-claim", {
          json: {
            candidate,
            data,
          },
        })
        .json();
    },
    async getOffers() {
      try {
        const response = await privateBoaHttp
          .get(`candidates/${this.currentCandidate.id}/offers`)
          .json<{
            offers: any[];
            claims: any[];
            requests: any[];
            files: any[];
          }>();
        return response;
      } catch (error) {
        console.error(error);
      } finally {
        //
      }
    },
    async approveOffer(offer: {
      PK?: string;
      SK?: string;
      headline: string;
      description: string;
      borderColor: string;
      headlineColor: string;
    }) {
      try {
        const response = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/approve-offer`, {
            json: offer,
          })
          .json<{
            success: boolean;
            message?: string;
            offerItem: Record<string, any>;
          }>();
        return response;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async submitOffer(offer: {
      PK?: string;
      SK?: string;
      headline: string;
      description: string;
      borderColor: string;
      headlineColor: string;
    }) {
      try {
        const response = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/submit-offer`, {
            json: offer,
          })
          .json<{
            success: boolean;
            message?: string;
            offerItem: Record<string, any>;
          }>();
        return response;
      } catch (error: any) {
        console.error(error);
        return {
          success: false,
          message: error.message ?? "An unknown error occurred",
        };
      }
    },
    async addOfferFile(fileUrl: string) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/offer-file`, {
            json: {
              fileUrl,
            },
          })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async addOfferRequest(msg: string) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/offer-request`, {
            json: {
              msg,
            },
          })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async addNotificationRequest(msg: string) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/notification-request`, {
            json: {
              msg,
            },
          })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async createOffer(offer: any) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/create-offer`, {
            json: {
              ...offer,
            },
          })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async updateOffer(offer: any) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/update-offer`, {
            json: {
              ...offer,
            },
          })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteOffer(offer: any) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/delete-offer`, {
            json: {
              ...offer,
            },
          })
          .json();
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async updateSponsorSettings(settings: {
      active?: boolean;
      logoUrl?: string;
    }) {
      try {
        const response: { success: boolean; message: string } =
          await privateBoaHttp
            .post(`candidates/${this.currentCandidate.id}/sponsor-settings`, {
              json: {
                settings,
              },
            })
            .json();
        return response;
      } catch (error: any) {
        console.error(error);
        const response = await error.response.json();
        return response;
      }
    },
    async updateDoorprizeSponsor(enabled: boolean): Promise<
      | {
          success: true;
        }
      | { success: false; message: string }
    > {
      try {
        await privateBoaHttp
          .post(`candidates/${this.currentCandidate.id}/doorprize-sponsor`, {
            json: {
              enabled,
            },
          })
          .json();
        return { success: true };
      } catch (error: any) {
        if (error instanceof HTTPError) {
          const response: { message: string } = await error.response.json();
          return { success: false, message: response.message };
        }
        return { success: false, message: "Unknown error" };
      }
    },
    async saveVoterRewardsSettings(settings: Partial<VoterRewardsSettings>) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(
            `candidates/${this.currentCandidate.id}/voter-rewards-settings`,
            {
              json: {
                ...settings,
              },
            }
          )
          .json();
        // if (response.success) {
        //   this.currentCandidate.voterRewards = {
        //     ...this.currentCandidate.voterRewards,
        //     ...settings,
        //   };
        // }
        return response;
      } catch (error: any) {
        const response = await error.response.json();
        return response;
      }
    },
    async updateVoterRewardsSms(smsMsg: string) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(
            `candidates/${this.currentCandidate.id}/voter-rewards-notifications`,
            {
              json: {
                smsMsg,
              },
            }
          )
          .json();
        if (response.success) {
          this.currentCandidate.voterRewards = {
            ...this.currentCandidate.voterRewards,
            smsMsg,
          };
        }
        return response;
      } catch (error: any) {
        const response = await error.response.json();
        return response;
      }
    },
    async updateVoterRewardsEmail({
      emailMsg,
      emailSubject,
    }: {
      emailMsg: string;
      emailSubject: string;
    }) {
      try {
        const response: { success: boolean } = await privateBoaHttp
          .post(
            `candidates/${this.currentCandidate.id}/voter-rewards-notifications`,
            {
              json: {
                emailMsg,
                emailSubject,
              },
            }
          )
          .json();
        if (response.success) {
          this.currentCandidate.voterRewards = {
            ...this.currentCandidate.voterRewards,
            emailMsg,
          };
        }
        return response;
      } catch (error: any) {
        const response = await error.response.json();
        return response;
      }
    },
    async purchaseProducts(
      customerId: string,
      paymentMethodId: string,
      purchases: PurchaseStep[],
      shippingAddress?: MailingAddress,
      data: Record<string, any> = {}
    ): Promise<PurchaseResponse> {
      try {
        const candidate = this.currentCandidate;
        const response: unknown = await privateBoaHttp
          .post(`candidates/${candidate.id}/purchase`, {
            json: {
              customerId,
              paymentMethodId,
              purchases,
              shippingAddress,
              data,
            },
          })
          .json();
        if (!isPurchaseResponse(response)) {
          throw new Error(
            `An unknown error occurred: ${JSON.stringify(response)}`
          );
        }
        return response;
      } catch (error) {
        if (error instanceof HTTPError) {
          const response = await error.response.json();
          if (isPurchaseResponse(response)) {
            return {
              ...response,
              success: false,
            };
          }
        }
        return {
          success: false,
          message: "An unknown error occurred.",
        };
      }
    },
    async updateSponsorshipInterest(
      candidateId: string,
      data: CandidateClaim | CandidateRegistration,
      interested: boolean
    ) {
      try {
        const response: { success: boolean } = await publicBoaHttp
          .post(`candidates/${candidateId}/update-sponsorship-interest`, {
            json: {
              data,
              interested,
            },
          })
          .json();
        return response.success;
      } catch (error: any) {
        const response = await error.response.json();
        return response;
      }
    },
    async verifyOwnership(candidateId: string): Promise<boolean> {
      try {
        const response = await privateBoaHttp
          .post(`candidates/${candidateId}/verify-ownership`)
          .json<{ success: boolean }>();
        return response.success;
      } catch (error: any) {
        const response = await error.response.json();
        console.error(response);
        return false;
      }
    },
    clearCandidate() {
      this.currentCandidate = { ...emptyCandidate } as Candidate;
    },
    clear() {
      this.$reset();
    },
  },
});
